import { api } from './index';

export interface CasualParkingVoucherReport {
  buildingId: string;
  billingPeriod: string;
  voucherTotal: number;
  settledVoucherTotal: number;
  outstandingVoucherTotal: number;
  deductionTotal: number;
  deductionDetails: Array<{
    id: string;
    counterpartyName: string;
    amount: number;
    notes: string | null;
    apartmentCode: string;
    invoiceId: string;
    machineAmount: number | null;
    actualAmount: number;
  }>;
  rows: Array<{
    counterpartyName: string;
    totalAmount: number;
    settledAmount: number;
    outstandingAmount: number;
    lineCount: number;
    details: Array<{
      id: string;
      amount: number;
      notes: string | null;
      apartmentCode: string;
      invoiceId: string;
      machineAmount: number | null;
      actualAmount: number;
      /** Ngày thu xe vãng lai (đầu phiên), not đối soát đối tác */
      collectedOn: string | null;
      receiptBillId: string | null;
      receiptNumber: string | null;
      settledAt: string | null;
      settledByName: string | null;
      settledNotes: string | null;
    }>;
  }>;
}

export interface CreateFixedServiceFeeData {
  apartmentId: string;
  feeCategoryId: string;
  billingPeriod: string;
  fromDate: string;
  dueDate: string;
  quantity?: number;
  accessCardId?: string;
  contractId?: string;
}

export interface CreateUsageServiceFeeData {
  apartmentId: string;
  feeCategoryId: string;
  billingPeriod: string;
  startReading: number;
  endReading: number;
  amount?: number;
  utilityMeterId?: string;
  fromDate: string;
  dueDate: string;
}

export interface CreateOtherServiceFeeData {
  apartmentId?: string;
  billingPeriod: string;
  amount: number;
  notes?: string;
  fromDate: string;
  dueDate: string;
  applyToAllApartments: boolean;
}

export interface AdjustServiceFeeData {
  invoiceId: string;
  adjustmentType: 'AMOUNT_CHANGE' | 'PARTIAL_PREPAYMENT';
  adjustmentAmount: number;
  notes?: string;
}

export interface ServiceFeeInvoiceQuery {
  apartmentId?: string;
  buildingId?: string;
  billingPeriod?: string;
  invoiceType?: 'FIXED' | 'USAGE_BASED' | 'OTHER';
  status?: string;
}

export const serviceFeeApi = {
  // Create fixed service fee
  createFixedServiceFee: async (data: CreateFixedServiceFeeData) => {
    const response = await api.post('/service-fees/fixed', data);
    return response.data;
  },
  createUsageServiceFee: async (data: CreateUsageServiceFeeData) => {
    const response = await api.post('/service-fees/usage', data);
    return response.data;
  },

  // Create other service fee
  createOtherServiceFee: async (data: CreateOtherServiceFeeData) => {
    const response = await api.post('/service-fees/other', data);
    return response.data;
  },

  createCasualParkingFee: async (data: {
    apartmentId: string;
    billingPeriod: string;
    revenueFrom: string;
    revenueTo: string;
    machineAmount: number;
    actualAmount: number;
    notes?: string;
    varianceLines?: Array<{
      varianceType: 'DEDUCTION' | 'VOUCHER';
      counterpartyName: string;
      amount: number;
      notes?: string;
      contractApartmentId?: string;
    }>;
  }) => {
    const response = await api.post('/service-fees/casual-parking', data);
    return response.data;
  },

  getCasualParkingVoucherContractTargets: async (
    buildingId: string,
    counterpartyName: string,
  ) => {
    const response = await api.get<{
      partners: Array<{
        optionKey: string;
        counterpartyName: string;
        displayLabel: string;
        targets: Array<{
          contractApartmentId: string;
          contractId: string;
          contractCode: string;
          customerName: string | null;
          apartmentId: string;
          apartmentCode: string;
          collectionTiming: 'PREPAID' | 'POSTPAID';
        }>;
      }>;
      targets: Array<{
        contractApartmentId: string;
        contractId: string;
        contractCode: string;
        customerName: string | null;
        apartmentId: string;
        apartmentCode: string;
        collectionTiming: 'PREPAID' | 'POSTPAID';
      }>;
    }>('/service-fees/casual-parking/voucher-contract-targets', {
      params: { buildingId, counterpartyName },
    });
    return response.data;
  },

  getCasualParkingVoucherReport: async (
    buildingId: string,
    billingPeriod: string,
    options?: { unsettledOnly?: boolean },
  ) => {
    const response = await api.get<CasualParkingVoucherReport>(
      '/service-fees/casual-parking/voucher-report',
      {
        params: {
          buildingId,
          billingPeriod,
          unsettledOnly: options?.unsettledOnly ? '1' : undefined,
        },
      },
    );
    return response.data;
  },

  updateCasualParkingVarianceSettlement: async (
    lineId: string,
    data: { settled: boolean; settledNotes?: string },
  ) => {
    const response = await api.patch(
      `/service-fees/casual-parking/variance-lines/${lineId}/settlement`,
      data,
    );
    return response.data;
  },

  bulkCasualParkingVoucherSettlement: async (data: {
    buildingId: string;
    billingPeriod: string;
    counterpartyName: string;
    settled: boolean;
    settledNotes?: string;
  }) => {
    const response = await api.patch(
      '/service-fees/casual-parking/voucher-settlement/bulk',
      data,
    );
    return response.data as {
      counterpartyName: string;
      billingPeriod: string;
      settled: boolean;
      updatedCount: number;
    };
  },

  // Adjust service fee
  adjustServiceFee: async (data: AdjustServiceFeeData) => {
    const response = await api.put('/service-fees/adjust', data);
    return response.data;
  },

  // Get service fee invoices
  getServiceFeeInvoices: async (query?: ServiceFeeInvoiceQuery) => {
    const params = new URLSearchParams();
    if (query?.apartmentId) params.append('apartmentId', query.apartmentId);
    if (query?.buildingId) params.append('buildingId', query.buildingId);
    if (query?.billingPeriod) params.append('billingPeriod', query.billingPeriod);
    if (query?.invoiceType) params.append('invoiceType', query.invoiceType);
    if (query?.status) params.append('status', query.status);

    const response = await api.get(`/service-fees?${params.toString()}`);
    return response.data;
  },

  // Get previous reading for usage-based fees
  getPreviousReading: async (
    apartmentId: string,
    feeCategoryId: string,
    billingPeriod: string,
    utilityMeterId?: string,
  ) => {
    const params = utilityMeterId
      ? `?utilityMeterId=${encodeURIComponent(utilityMeterId)}`
      : '';
    const response = await api.get(
      `/service-fees/previous-reading/${apartmentId}/${feeCategoryId}/${encodeURIComponent(billingPeriod)}${params}`,
    );
    return response.data;
  },

  // Delete service fee invoice
  deleteServiceFeeInvoice: async (id: string) => {
    const response = await api.delete(`/service-fees/${id}`);
    return response.data;
  },

  // Create apartment fee categories for all apartments in a building
  createApartmentFeeCategoriesForBuilding: async (buildingId: string, feeCategoryId: string) => {
    const response = await api.post(`/service-fees/create-apartment-fee-categories/${buildingId}/${feeCategoryId}`);
    return response.data;
  },

  // Get fee categories with apartment assignment status
  getFeeCategoriesWithApartmentStatus: async (buildingId: string, apartmentId?: string) => {
    const params = new URLSearchParams();
    if (apartmentId) params.append('apartmentId', apartmentId);
    
    const response = await api.get(`/service-fees/fee-categories/${buildingId}?${params.toString()}`);
    return response.data;
  },

  getInvoiceActionLogs: async (apartmentId: string) => {
    const response = await api.get(
      `/service-fees/invoice-action-logs?apartmentId=${encodeURIComponent(apartmentId)}`,
    );
    return response.data;
  },
}; 